import { EventType } from "@/analytics/analytics-events";
import { StartOrConfirmAlignmentEventProperties } from "@/analytics/cad-bim-events-properties";
import { changeMode } from "@/store/mode-slice";
import {
  selectIsControlPointSheetAlignment,
  selectWizardElementToAlignId,
  selectWizardPointCloudStreamToAlignId,
  selectWizardReferenceElementId,
  selectWizardReferencePointCloudStreamId,
} from "@/store/modes/alignment-wizard-mode-selectors";
import { resetAlignmentWizard } from "@/store/modes/alignment-wizard-mode-slice";
import {
  AlignmentReference,
  setAlignmentReference,
  setCloudForCloudCadAlignment,
} from "@/store/modes/cloud-cad-alignment-mode-slice";
import {
  setAlignedCloudStreamIdForCloudToSheetAlignment,
  setAlignedElementIdForCloudToSheetAlignment,
  setReferenceElementIdForCloudToSheetAlignment,
} from "@/store/modes/cloud-to-sheet-alignment-mode-slice";
import { setElementToAlignByControlPointsAlignment } from "@/store/modes/control-points-alignment-mode-slice";
import {
  setSheetCadAlignmentReference,
  setSheetForSheetCadAlignment,
  SheetCadAlignReference,
} from "@/store/modes/sheet-cad-alignment-mode-slice";
import {
  setReferenceCloudForAlignment,
  setSheetIdForAlignment,
  setStepForSheetToCloudAlignment,
  SheetToCloudAlignmentStep,
} from "@/store/modes/sheet-to-cloud-alignment-mode-slice";
import {
  setAlignedSheetId,
  setReferenceSheetId,
} from "@/store/modes/sheet-to-sheet-alignment-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { AppDispatch, store } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { redirectToRegistrationTool } from "@/utils/redirects";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  IElement,
  IElementAreaSection,
  IElementImgSheet,
  IElementImgSheetTiled,
  IElementSectionDataSession,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementGenericImgSheet,
  isIElementGenericModel3d,
  isIElementModel3dStream,
  isIElementSectionDataSession,
  isIElementSectionDataSetCapture,
  isIElementSectionDataSetEls,
  isIElementSectionDataSetFocus,
  isIElementSectionWithTypeHint,
} from "@faro-lotv/ielement-types";
import {
  selectChildDepthFirst,
  selectIElement,
  selectProjectId,
} from "@faro-lotv/project-source";
import { Stack } from "@mui/system";
import { useCallback, type JSX } from "react";
import { AlignWizardProgressBar } from "./align-wizard-progress-bar";
import { AlignWizardSplitScreen } from "./align-wizard-split-screen";

/** @returns The overlay for the cloud to CAD alignment mode */
export function AlignWizardModeOverlay(): JSX.Element {
  const dispatch = useAppDispatch();

  const projectId = useAppSelector(selectProjectId);
  assert(projectId, "invalid number project id");

  const elementToAlignId = useAppSelector(selectWizardElementToAlignId);
  const pointCloudStreamToAlignId = useAppSelector(
    selectWizardPointCloudStreamToAlignId,
  );
  const referenceElementId = useAppSelector(selectWizardReferenceElementId);
  const referencePointCloudStreamId = useAppSelector(
    selectWizardReferencePointCloudStreamId,
  );
  const alignedElement = useAppSelector(selectIElement(elementToAlignId));
  assert(alignedElement, "Element to align must exist to start alignment");

  const referenceElement = useAppSelector(selectIElement(referenceElementId));

  const isControlPointSheetAlignment = useAppSelector(
    selectIsControlPointSheetAlignment,
  );

  const selectAndRunAlignment = useCallback(() => {
    if (isIElementGenericImgSheet(alignedElement)) {
      alignSheet(
        dispatch,
        alignedElement,
        isControlPointSheetAlignment,
        referenceElement,
      );
    } else if (isIElementAreaSection(alignedElement)) {
      alignArea(
        dispatch,
        alignedElement,
        isControlPointSheetAlignment,
        referenceElement,
        referencePointCloudStreamId,
      );
    } else if (isIElementSectionDataSession(alignedElement)) {
      alignCloud(
        dispatch,
        projectId,
        alignedElement,
        referenceElement,
        pointCloudStreamToAlignId,
      );
    } else if (isIElementModel3dStream(alignedElement)) {
      alignCadModel(dispatch, referenceElement);
    } else if (
      isIElementSectionDataSetCapture(alignedElement) ||
      isIElementSectionDataSetEls(alignedElement) ||
      isIElementSectionDataSetFocus(alignedElement) ||
      isIElementSectionWithTypeHint(
        alignedElement,
        IElementTypeHint.dataSetSiteScape,
      )
    ) {
      // supported capture tree point clouds can only be aligned to a cad model
      assert(
        referenceElement && isIElementModel3dStream(referenceElement),
        "Reference must be 3D model",
      );
      alignCloudToModel(dispatch, pointCloudStreamToAlignId);
    } else {
      throw new Error("Not supported aligned element in Alignment Wizard");
    }

    dispatch(resetAlignmentWizard());
  }, [
    alignedElement,
    referenceElement,
    dispatch,
    isControlPointSheetAlignment,
    referencePointCloudStreamId,
    projectId,
    pointCloudStreamToAlignId,
  ]);

  return (
    <Stack
      direction="column"
      sx={{
        position: "absolute",
        width: "100%",
        height: "100%",
      }}
    >
      <AlignWizardProgressBar goToNextStep={selectAndRunAlignment} />
      <AlignWizardSplitScreen />
    </Stack>
  );
}

function alignSheet(
  dispatch: AppDispatch,
  sheet: IElementImgSheet | IElementImgSheetTiled,
  isControlPointAlignment: boolean,
  referenceElement?: IElement,
): void {
  // A sheet can be aligned to control points, or to another sheet
  if (isControlPointAlignment) {
    Analytics.track(EventType.openControlPointsAlignmentSheet);
    dispatch(setElementToAlignByControlPointsAlignment(sheet.id));
    dispatch(changeMode("controlPointsAlignment"));
  } else {
    assert(
      isIElementGenericImgSheet(referenceElement),
      "Reference element must be a sheet for sheet to sheet alignment",
    );
    Analytics.track<StartOrConfirmAlignmentEventProperties>("startAlignment", {
      combination: "sheet-to-sheet",
    });

    dispatch(setAlignedSheetId(sheet.id));
    dispatch(setReferenceSheetId(referenceElement.id));
    dispatch(changeMode("sheetToSheetAlignment"));
  }
}

function alignArea(
  dispatch: AppDispatch,
  area: IElementAreaSection,
  isControlPointAlignment: boolean,
  referenceElement?: IElement,
  referencePointCloudStreamId?: string,
): void {
  // An area can be aligned to control points, a cad model, or a point cloud
  if (isControlPointAlignment) {
    Analytics.track(EventType.openControlPointsAlignmentArea);
    dispatch(setElementToAlignByControlPointsAlignment(area.id));
    dispatch(changeMode("controlPointsAlignment"));
  } else {
    assert(
      referenceElement &&
        (isIElementGenericModel3d(referenceElement) ||
          isIElementSectionDataSession(referenceElement)),
      "Can only align area to 3D model or point cloud",
    );

    const sheet = selectChildDepthFirst(
      area,
      isIElementGenericImgSheet,
    )(store.getState());
    assert(sheet, "Sheet not found for selected floor.");

    if (isIElementGenericModel3d(referenceElement)) {
      // sheet to cad alignment
      Analytics.track<StartOrConfirmAlignmentEventProperties>(
        "startAlignment",
        {
          combination: "sheet-to-model",
        },
      );

      dispatch(setSheetCadAlignmentReference(SheetCadAlignReference.bimModel));
      dispatch(setSheetForSheetCadAlignment(sheet.id));
      dispatch(changeMode("sheetCadAlignment"));
    } else {
      // sheet to cloud alignment
      Analytics.track<StartOrConfirmAlignmentEventProperties>(
        "startAlignment",
        {
          combination: "sheet-to-cloud",
        },
      );

      const cloud = selectIElement(referencePointCloudStreamId)(
        store.getState(),
      );
      assert(
        cloud,
        "Sheet-to-cloud alignment should not be called when there is no point cloud selected",
      );

      dispatch(
        setStepForSheetToCloudAlignment(SheetToCloudAlignmentStep.setElevation),
      );
      dispatch(setSheetIdForAlignment(sheet.id));
      dispatch(setReferenceCloudForAlignment(cloud.id));
      dispatch(changeMode("sheetToCloudAlignment"));
    }
  }
}

function alignCloudToModel(
  dispatch: AppDispatch,
  pointCloudStreamToAlignId?: string,
): void {
  const cloud = selectIElement(pointCloudStreamToAlignId)(store.getState());

  assert(
    cloud,
    "Cloud-Cad alignment should not be called when there is no point cloud selected",
  );

  Analytics.track<StartOrConfirmAlignmentEventProperties>("startAlignment", {
    combination: "cloud-to-model",
  });

  dispatch(setAlignmentReference(AlignmentReference.bimModel));
  dispatch(setCloudForCloudCadAlignment(cloud.id));
  dispatch(changeMode("cloudCadAlignment"));
}

function alignCloud(
  dispatch: AppDispatch,
  projectId: string,
  alignedElement: IElementSectionDataSession,
  referenceElement: IElement | undefined,
  pointCloudStreamToAlignId?: string,
): void {
  assert(referenceElement, "Reference element must be provided for alignment");

  // A point cloud under data session section can be aligned to an area, a cad model,
  // or registered with another point cloud via RegistrationTool
  if (isIElementAreaSection(referenceElement)) {
    Analytics.track<StartOrConfirmAlignmentEventProperties>("startAlignment", {
      combination: "cloud-to-sheet",
    });

    dispatch(setAlignedElementIdForCloudToSheetAlignment(alignedElement.id));
    dispatch(
      setAlignedCloudStreamIdForCloudToSheetAlignment(
        pointCloudStreamToAlignId,
      ),
    );
    dispatch(
      setReferenceElementIdForCloudToSheetAlignment(referenceElement.id),
    );
    dispatch(changeMode("cloudToSheetAlignment"));
  } else if (isIElementModel3dStream(referenceElement)) {
    alignCloudToModel(dispatch, pointCloudStreamToAlignId);
  } else if (isIElementSectionDataSession(referenceElement)) {
    Analytics.track(EventType.startPairwiseRegistrationWorkflow);

    redirectToRegistrationTool({
      projectId,
      pointCloudId1: referenceElement.id,
      pointCloudId2: alignedElement.id,
    });
  } else {
    throw new Error(
      "Invalid combination of reference and aligned elements in Alignment Wizard",
    );
  }
}

function alignCadModel(
  dispatch: AppDispatch,
  referenceElement: IElement | undefined,
): void {
  assert(referenceElement, "Reference element must be provided for alignment");

  // A cad model can be aligned to an area, or to a point cloud
  if (isIElementAreaSection(referenceElement)) {
    const sheet = selectChildDepthFirst(
      referenceElement,
      isIElementGenericImgSheet,
    )(store.getState());
    assert(sheet, "Sheet not found for selected floor.");

    Analytics.track<StartOrConfirmAlignmentEventProperties>("startAlignment", {
      combination: "model-to-sheet",
    });

    dispatch(setActiveElement(sheet.id));

    dispatch(setSheetCadAlignmentReference(SheetCadAlignReference.sheet));
    dispatch(setSheetForSheetCadAlignment(sheet.id));
    dispatch(changeMode("sheetCadAlignment"));
  } else {
    const cloud = selectIElement(
      selectWizardReferencePointCloudStreamId(store.getState()),
    )(store.getState());

    assert(
      cloud,
      "Cad-To-Cloud alignment should not be called when there is no point cloud selected",
    );

    Analytics.track<StartOrConfirmAlignmentEventProperties>("startAlignment", {
      combination: "model-to-cloud",
    });

    dispatch(setAlignmentReference(AlignmentReference.pointCloud));
    dispatch(setCloudForCloudCadAlignment(cloud.id));
    dispatch(changeMode("cloudCadAlignment"));
  }
}
